import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  getBookingDraft,
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import {
  customerApiErrorMessage,
} from '../../lib/customer-api';
import { toast } from '../../lib/toast';
import {
  listCategories,
  listServices,
  listTreatmentCategories,
  type ServiceItem,
  type TreatmentCategory,
} from '../../lib/booking-api';

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const formatDuration = (m: number): string => {
  if (!m || m < 1) return '';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
};

// Sentinel id for the implicit "Other" group that collects treatments
// not assigned to any sub-category, so nothing is ever hidden.
const OTHER_GROUP = '__other__';

export default function SelectTreatmentPage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();

  const [services, setServices] = useState<ServiceItem[]>([]);
  const [groups, setGroups] = useState<TreatmentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  // Which treatment sub-category the customer is browsing (C2). Null =
  // still on the category-picker step. Ignored when the category has no
  // sub-categories (flat list).
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  // Category name from the draft, used in the page heading so the
  // user sees "Select Hair", "Select Beauty", etc. instead of a
  // generic "Select Treatment".
  const [categoryName, setCategoryName] = useState<string>('');

  // Whoever pre-populated the draft (LandingPage / LandingPageLoggedIn
  // / BookingLocationPage) decided which category we're picking a
  // treatment within. Reading it here avoids re-fetching every service.
  const categoryId = draft?.service_category_id ?? null;

  const load = async () => {
    setLoading(true);
    try {
      const [svcRes, grpRes] = await Promise.all([
        listServices(categoryId ?? undefined),
        categoryId
          ? listTreatmentCategories(categoryId)
          : Promise.resolve({ status: true, data: [] as TreatmentCategory[] }),
      ]);
      // Alphabetical by display name (case-insensitive, locale-aware
      // so accented characters group with their base letter). Slice
      // before sort to avoid mutating the API response.
      const list = [...(svcRes.data ?? [])].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, {
          sensitivity: 'base',
        }),
      );
      setServices(list);
      setGroups(
        [...(grpRes.data ?? [])].sort((a, b) => a.sort_order - b.sort_order),
      );
      // If the draft already has a service_id from a previous visit
      // (back button), preserve the selection — and jump to its group —
      // so the user doesn't lose their pick.
      const current = getBookingDraft()?.service_id ?? null;
      if (current && list.some((s) => s.id === current)) {
        setSelectedId(current);
        const svc = list.find((s) => s.id === current);
        setSelectedGroupId(svc?.treatment_category?.id ?? OTHER_GROUP);
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not load available treatments.'),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Resolve the picked category's display name once on mount so the
  // heading reads "Select Hair" / "Select Beauty" instead of the
  // generic "Select Treatment".
  useEffect(() => {
    if (!categoryId) return;
    let cancelled = false;
    listCategories()
      .then((res) => {
        if (cancelled) return;
        const cat = res.data.find((c) => c.id === categoryId);
        setCategoryName(cat?.name ?? '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const hasGroups = groups.length > 0;

  // Only show groups that actually contain treatments, plus an implicit
  // "Other" bucket for ungrouped treatments so none are unreachable.
  const visibleGroups = useMemo(() => {
    if (!hasGroups) return [];
    const out = groups
      .map((g) => ({
        id: g.id,
        name: g.name,
        icon: g.icon,
        count: services.filter((s) => s.treatment_category?.id === g.id).length,
      }))
      .filter((g) => g.count > 0);
    const ungrouped = services.filter((s) => !s.treatment_category?.id).length;
    if (ungrouped > 0) {
      out.push({ id: OTHER_GROUP, name: 'Other', icon: null, count: ungrouped });
    }
    return out;
  }, [groups, services, hasGroups]);

  // Treatments shown right now: the whole flat list (no groups), or the
  // treatments inside the selected group.
  const visibleTreatments = useMemo(() => {
    if (!hasGroups) return services;
    if (!selectedGroupId) return [];
    if (selectedGroupId === OTHER_GROUP) {
      return services.filter((s) => !s.treatment_category?.id);
    }
    return services.filter((s) => s.treatment_category?.id === selectedGroupId);
  }, [services, hasGroups, selectedGroupId]);

  // True when the customer is on the sub-category step (groups exist and
  // none picked yet).
  const onGroupStep = hasGroups && !selectedGroupId;

  const handleNext = () => {
    if (!selectedId) return;
    const svc = services.find((s) => s.id === selectedId);
    patchBookingDraft({
      service_id: selectedId,
      // Snapshot the catalogue defaults so downstream wizard steps
      // (duration on the availability picker, price on the basket)
      // have something to work with even before the user customises.
      duration_minutes: svc?.duration_minutes ?? undefined,
      currency: svc?.currency ?? undefined,
      service_category_id: svc?.category?.id ?? categoryId ?? null,
      // Snapshot the human-readable names too, so SelectProfessionalPage
      // can render the chosen service in its heading even if its own
      // catalog fetch is slow/empty — otherwise the heading falls back to
      // "—". Preserve any existing meta (cleaning answers, codes). (Issue 8)
      meta: {
        ...(getBookingDraft()?.meta ?? {}),
        service_name: svc?.name ?? null,
        service_category_name: svc?.category?.name ?? null,
      },
    });
    navigate('/select-professional');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        <SecondaryButton
          onClick={() => {
            // Back from a group's treatment list returns to the group
            // picker rather than leaving the flow.
            if (hasGroups && selectedGroupId) {
              setSelectedGroupId(null);
              setSelectedId('');
            } else {
              navigate(-1);
            }
          }}
          icon={
            <span className="material-icons text-[18px]">navigate_before</span>
          }
        >
          Back
        </SecondaryButton>
      </div>

      <div className="max-w-[1140px] mx-auto px-4 md:px-8 pt-15 pb-20">
        {/* Title Section */}
        <div className="text-center mb-10">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[25px] md:text-[40px] leading-[32px] md:leading-[54px] tracking-[-0.1px] mb-4">
            {onGroupStep
              ? categoryName
                ? `Choose a ${categoryName} category`
                : 'Choose a category'
              : 'Select your appointment type'}
          </h1>
          {onGroupStep ? (
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              Pick a category to see the treatments within it.
            </p>
          ) : (
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] max-w-[560px] mx-auto">
              Select your main service below. Once you&apos;ve chosen your pro
              you&apos;ll be able to add extras, for example a cut and blowdry.
            </p>
          )}
        </div>

        {/* Step 1 — category picker (only when the category has groups) */}
        {onGroupStep ? (
          <div className="max-w-[600px] mx-auto mb-10">
            {loading ? (
              <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
                Loading…
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {visibleGroups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => {
                      setSelectedGroupId(g.id);
                      setSelectedId('');
                    }}
                    className="flex items-center justify-between gap-3 px-5 rounded-xl border border-[#e0e0e0] bg-white text-[#333333] hover:border-[#8e2a2f] hover:text-[#8e2a2f] shadow-sm transition-all duration-200 min-h-[64px] py-3"
                  >
                    <span className="flex items-center gap-3">
                      {g.icon ? (
                        <span className="material-icons text-[#8e2a2f] text-[22px]">
                          {g.icon}
                        </span>
                      ) : null}
                      <span className="font-['DM_Sans',sans-serif] font-bold text-[16px] tracking-[-0.2px] text-left">
                        {g.name}
                      </span>
                    </span>
                    <span className="font-['DM_Sans',sans-serif] text-[12px] text-[#908e8e] whitespace-nowrap shrink-0">
                      {g.count} {g.count === 1 ? 'option' : 'options'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Step 2 (or flat list) — treatment picker */
          <div className="max-w-[600px] mx-auto mb-10">
            {loading ? (
              <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
                Loading treatments…
              </div>
            ) : visibleTreatments.length === 0 ? (
              <div className="py-16 text-center">
                <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                  No treatments available
                </p>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
                  {categoryId
                    ? "This category doesn't have any treatments yet — try another."
                    : 'Start your booking from the home page so we know which category to show.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visibleTreatments.map((svc) => {
                  const isSelected = selectedId === svc.id;
                  return (
                    <button
                      key={svc.id}
                      onClick={() => setSelectedId(svc.id)}
                      className={`flex items-center justify-between gap-3 px-4 rounded-xl border transition-all duration-200 min-h-[50px] py-2 ${
                        isSelected
                          ? 'border-[#8e2a2f] text-[#8e2a2f] shadow-[0_2px_8px_rgba(142,42,47,0.1)]'
                          : 'border-[#e0e0e0] text-[#333333] hover:border-[#bdbdbd] bg-white shadow-sm'
                      }`}
                    >
                      <span className="font-['DM_Sans',sans-serif] font-bold text-[16px] leading-[21px] tracking-[-0.2px] text-left">
                        {svc.name}
                      </span>
                      <span
                        className={`font-['DM_Sans',sans-serif] text-[12px] whitespace-nowrap shrink-0 ${
                          isSelected ? 'text-[#8e2a2f]' : 'text-[#908e8e]'
                        }`}
                      >
                        {/* Inclusive "from" price (cheapest pro's rate +
                            platform fee). Rates vary by pro, so we lead
                            with "from". */}
                        from {formatMoney(svc.from_price ?? svc.base_price, svc.currency)}
                        {svc.pricing_unit === 'PER_HOUR' ? '/hr' : ''}
                        {svc.duration_minutes
                          ? ` · ${formatDuration(svc.duration_minutes)}`
                          : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Next Button — only on the treatment step */}
        {!onGroupStep ? (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 md:static md:border-none md:p-0 md:bg-transparent z-10 flex justify-center">
            <div className="w-full max-w-[600px]">
              <PrimaryButton
                onClick={handleNext}
                className="w-full h-[54px]"
                disabled={!selectedId || loading}
              >
                Next
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </div>

      <Footer />
    </div>
  );
}
